import {
	type CreatedPersonalAccessToken,
	CreatePersonalAccessTokenInput,
	type PersonalAccessToken,
} from "@cleanbudget/contract/personal-access-token";
import { Alert, AlertDescription, AlertTitle } from "@cleanbudget/ui/components/alert";
import { Button } from "@cleanbudget/ui/components/button";
import { Card, CardContent } from "@cleanbudget/ui/components/card";
import { Field, FieldLabel } from "@cleanbudget/ui/components/field";
import { Input } from "@cleanbudget/ui/components/input";
import { IconAlertTriangle, IconCheck, IconCopy, IconTrash } from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { BackLink } from "@/components/back-link";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { Form, FormError, SubmitButton, TextField, useZodForm } from "@/components/form/form";
import { HelpButton } from "@/components/help/help-button";
import { PageHeader } from "@/components/page-header";
import { formatDateTime } from "@/lib/format";
import { orpc } from "@/lib/orpc";

const tokensQuery = () => orpc.personalAccessToken.list.queryOptions();

/**
 * Persoonlijke tokens: your own bearer tokens for scripts and Claude (MCP). Make one (the secret
 * shows once, here), see when each was last used, revoke one behind a confirm dialog.
 */
export const Route = createFileRoute("/_admin/settings/token/")({
	loader: ({ context }) => context.queryClient.ensureQueryData(tokensQuery()),
	component: TokensPage,
});

function TokensPage() {
	const { t } = useTranslation("personalAccessToken");
	const { t: tCommon } = useTranslation();
	const { t: tSettings } = useTranslation("settings");
	const queryClient = useQueryClient();
	const { data } = useSuspenseQuery(tokensQuery());
	const [created, setCreated] = useState<CreatedPersonalAccessToken>();
	const invalidate = () => queryClient.invalidateQueries({ queryKey: orpc.personalAccessToken.key() });
	const revoke = useMutation({ ...orpc.personalAccessToken.revoke.mutationOptions(), onSuccess: invalidate });

	const columns: readonly Column<PersonalAccessToken>[] = [
		{
			id: "name",
			header: t("column.name"),
			cell: (token) => (
				<span className="flex flex-col">
					<span className="font-medium">{token.name}</span>
					<span className="font-mono text-xs text-muted-foreground">{t("hint", { hint: token.hint })}</span>
				</span>
			),
		},
		{
			id: "createdAt",
			header: t("column.created"),
			secondary: true,
			cell: (token) => <time dateTime={token.createdAt}>{formatDateTime(token.createdAt)}</time>,
		},
		{
			id: "lastUsedAt",
			header: t("column.lastUsed"),
			cell: (token) =>
				token.lastUsedAt ? (
					<time dateTime={token.lastUsedAt}>{formatDateTime(token.lastUsedAt)}</time>
				) : (
					<span className="text-muted-foreground">{tCommon("never")}</span>
				),
		},
		{
			id: "actions",
			header: <span className="sr-only">{t("column.actions")}</span>,
			align: "right",
			cell: (token) => (
				<ConfirmDialog
					trigger={
						<Button variant="ghost" size="icon-sm" aria-label={t("revoke.button", { name: token.name })}>
							<IconTrash aria-hidden />
						</Button>
					}
					title={
						<span className="flex items-center gap-1.5">
							{t("revoke.title", { name: token.name })}
							<HelpButton topic="personalAccessToken:revoke" />
						</span>
					}
					description={t("revoke.description")}
					confirmLabel={t("revoke.confirm")}
					onConfirm={async () => {
						await revoke.mutateAsync({ id: token.id });
						if (created?.token.id === token.id) setCreated(undefined);
					}}
				/>
			),
		},
	];

	return (
		<div className="flex max-w-3xl flex-col gap-6">
			<PageHeader
				back={<BackLink to="/settings">{tSettings("title")}</BackLink>}
				title={t("title")}
				description={t("description")}
				help="personalAccessToken:tokens"
			/>
			{created ? <SecretCard created={created} onDone={() => setCreated(undefined)} /> : null}
			<NewTokenCard
				onCreated={(token) => {
					setCreated(token);
					void invalidate();
				}}
			/>
			<DataTable
				label={t("title")}
				columns={columns}
				rows={data}
				rowKey={(token) => token.id}
				empty={<p className="p-6 text-center text-sm text-muted-foreground">{t("empty")}</p>}
			/>
		</div>
	);
}

function NewTokenCard({ onCreated }: { onCreated: (created: CreatedPersonalAccessToken) => void }) {
	const { t } = useTranslation("personalAccessToken");
	const { t: tCommon } = useTranslation();
	const form = useZodForm(CreatePersonalAccessTokenInput, { defaultValues: { name: "" } });
	const create = useMutation(orpc.personalAccessToken.create.mutationOptions());

	const onSubmit = async (values: CreatePersonalAccessTokenInput) => {
		try {
			onCreated(await create.mutateAsync(values));
			form.reset();
		} catch {
			form.setError("root", { type: "server", message: tCommon("form.failed") });
		}
	};

	return (
		<Card>
			<CardContent>
				<Form form={form} onSubmit={onSubmit}>
					<TextField
						control={form.control}
						name="name"
						label={t("field.name")}
						help="personalAccessToken:new"
						description={t("field.nameDescription")}
						autoComplete="off"
					/>
					<FormError control={form.control} />
					<div>
						<SubmitButton control={form.control}>{t("new.submit")}</SubmitButton>
					</div>
				</Form>
			</CardContent>
		</Card>
	);
}

/** The new token's secret, once: copy it now, it is never shown again. */
function SecretCard({ created, onDone }: { created: CreatedPersonalAccessToken; onDone: () => void }) {
	const { t } = useTranslation("personalAccessToken");
	const [copied, setCopied] = useState(false);
	const id = useId();

	const copy = async () => {
		await navigator.clipboard.writeText(created.secret);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	};

	return (
		<Alert>
			<IconAlertTriangle aria-hidden />
			<AlertTitle>{t("created.title", { name: created.token.name })}</AlertTitle>
			<AlertDescription className="flex flex-col gap-3">
				<p>{t("created.description")}</p>
				<Field>
					<FieldLabel htmlFor={id} className="sr-only">
						{t("created.label")}
					</FieldLabel>
					<div className="flex gap-2">
						<Input
							id={id}
							readOnly
							value={created.secret}
							className="font-mono"
							onFocus={(event) => event.currentTarget.select()}
						/>
						<Button variant="outline" onClick={copy}>
							{copied ? <IconCheck aria-hidden /> : <IconCopy aria-hidden />}
							{copied ? t("created.copied") : t("created.copy")}
						</Button>
					</div>
				</Field>
				<div>
					<Button variant="outline" size="sm" onClick={onDone}>
						{t("created.done")}
					</Button>
				</div>
			</AlertDescription>
		</Alert>
	);
}
